/* BOMBER 360 SPIN: drag, swipe or arrow keys to rotate an image sequence */
if (!customElements.get('bomber-spin')) {
  customElements.define(
    'bomber-spin',
    class BomberSpin extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.frames = Array.from(this.querySelectorAll('.bomber-spin__frame'));
        this.n = this.frames.length;
        if (this.n < 2) return;

        this.current = 0;
        this.pos = 0;
        this.velocity = 0;
        this.raf = null;
        this.lastT = 0;
        this.dragging = false;
        this.visible = false;
        this.autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.fps = 12;

        this.tick = this.tick.bind(this);
        this.bindEvents();

        // Frames are painted onto one canvas. Hidden <img> frames can be dropped from the
        // browser's memory on big screens, which made some frames flash blank. drawImage never does.
        this.canvas = document.createElement('canvas');
        this.canvas.className = 'bomber-spin__canvas';
        this.canvas.setAttribute('aria-hidden', 'true');
        this.insertBefore(this.canvas, this.frames[this.n - 1].nextSibling);
        this.ctx = this.canvas.getContext('2d');
        const f0 = this.frames[0];
        if (f0.complete && f0.naturalWidth) this.draw(0);
        else f0.addEventListener('load', () => { if (this.current === 0) this.draw(0); }, { once: true });
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => { this._w = 0; this.draw(this.current); });
          this.ro.observe(this);
        }

        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              this.visible = e.isIntersecting;
              if (this.visible) {
                this.loadAll();
                this.run();
              } else {
                this.stop();
              }
            });
          }, { rootMargin: '200px' });
          this.io.observe(this);
        } else {
          this.visible = true;
          this.loadAll();
          this.run();
        }
      }

      disconnectedCallback() {
        this.stop();
        if (this.io) this.io.disconnect();
        if (this.ro) this.ro.disconnect();
      }

      draw(i) {
        const img = this.frames[i];
        if (!this.ctx || !img || !img.complete || !img.naturalWidth) return false;
        const size = Math.round((this.clientWidth || 500) * Math.min(window.devicePixelRatio || 1, 2));
        if (this._w !== size) {
          this._w = size;
          this.canvas.width = size;
          this.canvas.height = size;
        }
        const s = Math.min(size / img.naturalWidth, size / img.naturalHeight);
        const w = img.naturalWidth * s, h = img.naturalHeight * s;
        try {
          this.ctx.clearRect(0, 0, size, size);
          this.ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        } catch (e) { return false; }
        if (!this.classList.contains('has-canvas')) this.classList.add('has-canvas');
        return true;
      }

      // Ask the CDN for frames sized to the screen (phones get small files, desktop stays sharp)
      frameWidth() {
        const px = (this.clientWidth || 500) * Math.min(window.devicePixelRatio || 1, 2);
        return Math.min(1000, Math.max(400, Math.ceil(px / 100) * 100));
      }

      sized(url) {
        if (!url) return url;
        const w = this.frameWidth();
        if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
        return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
      }

      loadAll() {
        if (this._loaded) return;
        this._loaded = true;
        this.ready = this.frames.map((img, i) => i === 0 && img.complete && img.naturalWidth > 0);
        this.readyCount = this.ready.filter(Boolean).length;
        this.frames.forEach((img, i) => {
          const done = () => {
            if (this.ready[i]) return;
            this.ready[i] = true;
            this.readyCount++;
            if (this.readyCount >= this.n) this.classList.add('is-loaded');
          };
          img.decoding = 'async';
          if (img.dataset.src) {
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            img.src = this.sized(img.dataset.src);
            img.removeAttribute('data-src');
          } else if (img.complete) {
            done();
          } else {
            img.addEventListener('load', done, { once: true });
          }
        });
      }

      show(p) {
        const i = ((Math.round(p) % this.n) + this.n) % this.n;
        if (i === this.current) return;
        if (this.ready && !this.ready[i]) return;   // wait for that frame instead of flashing a blank
        if (!this.draw(i)) return;
        this.frames[this.current].classList.remove('is-active');
        this.frames[i].classList.add('is-active');
        this.current = i;
      }

      pxPerFrame() {
        const coarse = window.matchMedia('(pointer: coarse)').matches;
        return Math.max(coarse ? 9 : 4, this.clientWidth / (this.n * (coarse ? 0.75 : 0.9)));
      }

      tick(t) {
        const dt = this.lastT ? Math.min(0.1, (t - this.lastT) / 1000) : 0;
        this.lastT = t;
        if (!this.dragging) {
          if (Math.abs(this.velocity) > 0.2) {
            this.pos += this.velocity * dt;
            this.velocity *= Math.pow(0.04, dt);
          } else if (this.autoplay) {
            this.velocity = 0;
            if (!this.readyCount || this.readyCount >= this.n) this.pos += this.fps * dt;
          } else {
            this.velocity = 0;
            this.raf = null;
            this.lastT = 0;
            return;
          }
          this.show(this.pos);
        }
        this.raf = requestAnimationFrame(this.tick);
      }

      run() {
        if (!this.raf && this.visible) {
          this.lastT = 0;
          this.raf = requestAnimationFrame(this.tick);
        }
      }

      stop() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
        this.lastT = 0;
      }

      touched() {
        this.autoplay = false;
        this.classList.add('is-touched');
        this.loadAll();
      }

      bindEvents() {
        let startX = 0, startPos = 0, lastX = 0, lastMoveT = 0;

        this.addEventListener('pointerdown', (e) => {
          if (e.button !== undefined && e.button !== 0) return;
          this.touched();
          this.dragging = true;
          this.velocity = 0;
          startX = lastX = e.clientX;
          startPos = this.pos;
          lastMoveT = performance.now();
          this.classList.add('is-dragging');
          try { this.setPointerCapture(e.pointerId); } catch (err) {}
        });

        this.addEventListener('pointermove', (e) => {
          if (!this.dragging) return;
          const now = performance.now();
          const ppf = this.pxPerFrame();
          this.pos = startPos - (e.clientX - startX) / ppf;
          const dtm = (now - lastMoveT) / 1000;
          if (dtm > 0) this.velocity = -((e.clientX - lastX) / ppf) / dtm;
          lastX = e.clientX;
          lastMoveT = now;
          this.show(this.pos);
        });

        const end = () => {
          if (!this.dragging) return;
          this.dragging = false;
          this.classList.remove('is-dragging');
          if (performance.now() - lastMoveT > 80) this.velocity = 0;
          const cap = window.matchMedia('(pointer: coarse)').matches ? 30 : 60;
          this.velocity = Math.max(-cap, Math.min(cap, this.velocity));
          this.run();
        };
        this.addEventListener('pointerup', end);
        this.addEventListener('pointercancel', end);

        // Stop the gallery from treating a drag as a click or swipe
        this.addEventListener('click', (e) => e.stopPropagation());

        // Phones: a sideways swipe on the 360 belongs to the 360, not to the page or the gallery slider.
        // Up and down swipes still scroll the page normally.
        let tx = 0, ty = 0, axis = null;
        this.addEventListener('touchstart', (e) => {
          const t = e.touches[0]; tx = t.clientX; ty = t.clientY; axis = null;
          e.stopPropagation();
        }, { passive: true });
        this.addEventListener('touchmove', (e) => {
          const t = e.touches[0];
          if (!axis) {
            const dx = Math.abs(t.clientX - tx), dy = Math.abs(t.clientY - ty);
            if (dx + dy > 6) axis = dx > dy ? 'x' : 'y';
          }
          if (axis === 'x') { e.preventDefault(); e.stopPropagation(); }
        }, { passive: false });

        this.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          e.stopPropagation();
          this.touched();
          this.pos += e.key === 'ArrowRight' ? 1 : -1;
          this.show(this.pos);
        });
      }
    }
  );
}

/* BOMBER 360 SPIN: gallery order on products with a 360.
   360 first, then lifestyle photos and videos, then white background product shots.
   Photos are sorted by looking at their edges: near white edges = product shot. */
(function () {
  function isWhiteShot(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve(false);
      var url = src.replace(/([?&])width=\d+/, '$1width=64');
      if (url === src) url += (src.indexOf('?') > -1 ? '&' : '?') + 'width=64';
      var img = new Image();
      if (url.indexOf('//') === 0) url = location.protocol + url;
      try { if (new URL(url).origin !== location.origin) img.crossOrigin = 'anonymous'; } catch (e) {}
      img.onload = function () {
        try {
          var c = document.createElement('canvas'); c.width = 32; c.height = 32;
          var x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(img, 0, 0, 32, 32);
          var d = x.getImageData(0, 0, 32, 32).data, white = 0, total = 0;
          for (var i = 0; i < 32; i++) {
            [[i, 0], [i, 31], [0, i], [31, i], [i, 1], [i, 30], [1, i], [30, i]].forEach(function (p) {
              var o = (p[1] * 32 + p[0]) * 4, r = d[o], g = d[o + 1], b = d[o + 2], a = d[o + 3];
              total++;
              if (a < 20) { white++; return; }
              var hi = Math.max(r, g, b), lo = Math.min(r, g, b);
              if (lo > 222 && hi - lo < 20) white++;
            });
          }
          resolve(white / total > 0.8);
        } catch (e) { resolve(false); }
      };
      img.onerror = function () { resolve(false); };
      img.src = url;
    });
  }

  function slideSrc(li) {
    if (li.querySelector('deferred-media, video, iframe, model-viewer, .deferred-media')) return null; // videos and 3D count as lifestyle
    var img = li.querySelector('img');
    return img ? (img.currentSrc || img.src) : null;
  }

  function sortGallery(gallery) {
    if (gallery.dataset.bomberSorted) return;
    gallery.dataset.bomberSorted = '1';
    var list = gallery.querySelector('[id^="Slider-Gallery"]');
    var thumbs = gallery.querySelector('[id^="Slider-Thumbnails"]');
    if (!list) return;
    var slides = Array.prototype.filter.call(list.children, function (li) {
      return li.matches('[data-media-id]') && !li.classList.contains('bomber-spin-slide');
    });
    Promise.all(slides.map(function (li) {
      var src = slideSrc(li);
      return src ? isWhiteShot(src) : Promise.resolve(false);
    })).then(function (white) {
      var lifestyle = [], studio = [];
      slides.forEach(function (li, i) { (white[i] ? studio : lifestyle).push(li); });
      var ordered = lifestyle.concat(studio);
      var spin = list.querySelector('.bomber-spin-slide');
      var anchor = spin ? spin.nextSibling : list.firstChild;
      ordered.forEach(function (li) { list.insertBefore(li, anchor); anchor = li.nextSibling; });
      if (thumbs) {
        var spinThumb = thumbs.querySelector('.bomber-spin-thumb');
        var tAnchor = spinThumb ? spinThumb.nextSibling : thumbs.firstChild;
        ordered.forEach(function (li) {
          var t = thumbs.querySelector('[data-target="' + li.dataset.mediaId + '"]');
          if (t) { thumbs.insertBefore(t, tAnchor); tAnchor = t.nextSibling; }
        });
        var pos = 1;
        Array.prototype.forEach.call(thumbs.children, function (t) { if (t.dataset.mediaPosition) t.dataset.mediaPosition = pos++; });
      }
      ['GalleryViewer', 'GalleryThumbnails'].forEach(function (p) {
        var s = gallery.querySelector('[id^="' + p + '"]');
        if (s && typeof s.resetPages === 'function') { try { s.resetPages(); } catch (e) {} }
      });
    });
  }

  function run() {
    document.querySelectorAll('media-gallery').forEach(function (g) {
      if (g.querySelector('.bomber-spin-slide')) sortGallery(g);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
